import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
  Link,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import DetailItem from "../components/common/DetailItem";
import UpdateCompanyModal from "../components/company/UpdateCompanyModal";
import {
  getCompany,
  resetCompanyServerStatus,
} from "../redux/actions/companyActions";
import { usePermissions } from "../hooks/usePermissions";
import AppCircularProgress from "../components/common/AppCircularProgress";
import { useAppDispatch, useAppSelector } from "../redux/store";

const CompanyPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    company,
    serverStatus,
    loadingCompany,
    errorLoadingCompany,
    loadingCompanyUpdate,
    errorUpdatingCompany,
  } = useAppSelector((state) => state.company);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const canUpdate = hasPermission("company:update");

  useEffect(() => {
    if (!company) {
      dispatch(getCompany());
    }
  }, [dispatch, company]);

  const handleCloseEdit = useCallback(() => {
    if (!loadingCompanyUpdate) setIsUpdateModalOpen(false);
  }, [loadingCompanyUpdate]);

  useEffect(() => {
    if (serverStatus === 200) {
      handleCloseEdit();
      dispatch(resetCompanyServerStatus());
    }
  }, [serverStatus, dispatch, handleCloseEdit]);

  if (loadingCompany) {
    return (
      <PageContainer title="Company Profile">
        <AppCircularProgress />
      </PageContainer>
    );
  }

  if (errorLoadingCompany) {
    return (
      <PageContainer title="Company Profile">
        <Typography sx={{ color: "error.main" }}>
          {errorLoadingCompany}
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Company Profile"
      subtitle="View and manage the master details for your company workspace."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Company details", to: undefined },
      ]}
      actions={
        canUpdate &&
        company && (
          <AppButton
            variant="outlined"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={() => setIsUpdateModalOpen(true)}
          >
            Edit Details
          </AppButton>
        )
      }
    >
      {company ? (
        <Grid container spacing={3}>
          {/* --- MAIN DETAILS --- */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {company.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={3}>
                  <DetailItem
                    label="Company Code"
                    value={company.companyCode}
                  />
                  <DetailItem label="Tax ID" value={company.taxId || "N/A"} />
                  <DetailItem
                    label="Email Address"
                    value={company.contactEmail}
                  />
                  <DetailItem
                    label="Phone Number"
                    value={company.contactPhone}
                  />
                  <DetailItem
                    label="Website"
                    value={
                      company.website ? (
                        <Link
                          href={company.website}
                          target="_blank"
                          sx={{
                            color: "primary.main",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {company.website}
                        </Link>
                      ) : (
                        "N/A"
                      )
                    }
                  />
                  <DetailItem
                    label="Registered On"
                    value={new Date(company.createdAt).toLocaleDateString()}
                  />
                  <DetailItem
                    label="Address"
                    value={`${company.headOfficeAddress.streetLine1}, ${company.headOfficeAddress.city}`}
                  />
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* --- BRANCHES LIST --- */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Branches
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  {company.branches?.map((branch) => (
                    <Paper
                      key={branch.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        {branch.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {branch.address?.city}, {branch.address?.country}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {branch.contactPhone || "No contact provided"}
                      </Typography>
                    </Paper>
                  ))}
                  {(!company.branches || company.branches.length === 0) && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.disabled", fontStyle: "italic" }}
                    >
                      No branches registered yet.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ color: "text.secondary" }}>
            No company data available.
          </Typography>
        </Box>
      )}

      {isUpdateModalOpen && company && (
        <UpdateCompanyModal
          open={isUpdateModalOpen}
          onClose={handleCloseEdit}
          company={company}
          loading={loadingCompanyUpdate}
          error={errorUpdatingCompany}
        />
      )}
    </PageContainer>
  );
};

export default CompanyPage;
